import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Course } from '../../models/course.model';
import { CourseService } from '../../services/course.service';
import { InfoDialogComponent } from '../dialogs/info-dialog/info-dialog.component';
import { EditCourseDialogComponent } from '../dialogs/edit-course-dialog/edit-course-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InfoPopUpComponent } from '../pop-ups/info-pop-up/info-pop-up.component';
import { PopupHelper } from '../../helpers/pop-up-helper';
import { RegistryImageVersion } from 'src/app/models/registry-image-version.model';
import { ImageRegistryService } from 'src/app/services/image-registry.service';
import { SubmitDialogComponent } from '../dialogs/submit-dialog/submit-dialog.component';
import { Bundle } from 'src/app/models/bundle.model';
import { BundleService } from 'src/app/services/bundle.service';

@Component({
  selector: 'app-course',
  templateUrl: './course.component.html',
  styleUrls: ['./course.component.scss']
})
export class CourseComponent implements OnInit {
  awsWorkspaceForm: UntypedFormGroup;
  areCoursesLoaded = false;
  courseCreating = false; 
  showCopies = false;
  dataSource = new MatTableDataSource<Course>();
  expandedUrls = new Set<string>();
  public minorVersions: string[];
  public majorVersions: string[];
  public allBundles: Bundle[] = [];
  public majorVersionsWiithBuilds: RegistryImageVersion[];
  private allCourses: Course[] = [];


  courseColumns = [
    {
      columnDef: 'courseID',
      header: 'CourseID',
      cell: (element: Course) => `${element.courseID}`,
    },
    {
      columnDef: 'courseName',
      header: 'Course Name',
      cell: (element: Course) => `${element.courseName}`,
    },
    {
      columnDef: 'gitURL',
      header: 'gitURL',
      cell: (element: Course) => `${element.gitURL}`,
    },
    {
      columnDef: 'majorVersion',
      header: 'Major Version',
      cell: (element: Course) => `${element.majorVersion}`,
    },
    {
      columnDef: 'minorVersion',
      header: 'Minor Version',
      cell: (element: Course) => `${element.minorVersion}`,
    },
    {
      columnDef: 'vcs',
      header: 'VCS',
      cell: (element: Course) => `${element.vcs}`,
    },
    {
      columnDef: 'bundle',
      header: 'Bundle',
      cell: (element: Course) => `${element.bundleDto?.bundleName}`,
    },
    {
      columnDef: 'actions',
      header: '',
      cell: (element: Course) => `${element}`,
    },
  ];

  displayedColumnsCourses = this.courseColumns.map(c => c.columnDef);

  constructor(
    private courseService: CourseService, 
    private dialog: MatDialog,
    private popUp: MatSnackBar,
    private popUpHelper: PopupHelper,
    private registryService: ImageRegistryService,
    private bundleService: BundleService
  )  {
    this.registryService.getRegistryImageVersions().subscribe(
      data => {
        this.majorVersions = data.map(mtextVersion => mtextVersion.majorVersion);
        this.majorVersions.sort((a, b) => (a > b ? -1 : 1));
        this.majorVersionsWiithBuilds = data;
        this.awsWorkspaceForm.get('majorVersion').setValue(this.majorVersions[0]);
        this.awsWorkspaceForm.get('majorVersion').enable();
        this.onVersionChange(this.majorVersions[0]);
    });
    this.bundleService.getBundles().subscribe(
      data => {
        this.allBundles = data;
      }
    )
    
    this.refreshCourses();
   }

  refreshCourses() {
    this.courseService.getCourses().subscribe(
      (data) => {
        this.allCourses = data;
        this.applyCourseFilter();
        this.areCoursesLoaded = true;
      },
      (error) => {
        this.popUpHelper.showErrorPopUp("There was an error with getting the courses!\n", error)
      }
    )
  }

    applyCourseFilter() {
      if (this.showCopies) {
        this.dataSource.data = this.allCourses;
      } else {
        this.dataSource.data = this.allCourses.filter(course => !course.isCopy);
      }
    }

  createCourse() {
    this.courseCreating = true;
    let course = new Course();

    course.courseID = this.awsWorkspaceForm.get('courseID').value;
    course.courseName = this.awsWorkspaceForm.get('courseName').value;
    course.gitURL = this.awsWorkspaceForm.get('gitURL').value;
    course.majorVersion = this.awsWorkspaceForm.get('majorVersion').value;
    course.minorVersion = this.awsWorkspaceForm.get('minorVersion').value;
    course.vcs = this.awsWorkspaceForm.get('vcs').value;
    course.bundleDto = this.awsWorkspaceForm.get('bundle').value;

    this.courseService.createCourse(course).subscribe(
      (data) => {
        this.courseCreating = false;
        this.refreshCourses();
        this.popUpHelper.showPopUp('Course was created!');

      }, 
      (error) => {
        this.courseCreating = false;
        this.refreshCourses();
        this.showErrorMessage("There was an error with creating the course: \n" + error.error);
      } 
    )
  }

  editCourse(courseToEdit: Course) {
    const dialogRef = this.openEditDialog(courseToEdit);
    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.popUpHelper.showPopUp('Course "' + courseToEdit.courseName + '" will be updated');

      this.courseService.editCourse(result).subscribe(
        () => {
          this.refreshCourses();
          this.popUpHelper.showPopUp('Course "' + courseToEdit.courseName + '" was successfully updated');
        },
        error => {
          this.openInfoDialog('Error updating course', error.error);
        });
      }
    })
  }

  deleteCourse(courseToDelete: Course) {
    const dialogRef = this.openSubmitDialog(
      'Confirm Course deletion',
      'Course with name "' + courseToDelete.courseName + '" is going to be deleted. Are you sure?');
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.popUpHelper.showLoadingPopUp("Course is deleting!")
        this.courseService.deleteCourse(courseToDelete).subscribe(
          (data) => {
            this.refreshCourses();
            this.popUpHelper.showPopUp('Course was deleted!');
          },
          (error) => {
            this.refreshCourses();
            this.popUp.dismiss();
            this.showErrorMessage("There was an error with deleting the course, it is possible that course is assigned to Training, please check if it is so. In case it isn't, please contact Administrator");
          }
        )
      }
    });
  }

  getShortUrl(course: any): string {
    const fullUrl = course.gitURL;
    const id = course.courseID;

    if (this.expandedUrls.has(id)) {
      return fullUrl;
    }

    if (!fullUrl) return '';

    const parts = fullUrl.split('/');
    return parts[parts.length - 1];
  }

  toggleFullUrl(course: any): void {
    const id = course.courseID;
    if (this.expandedUrls.has(id)) {
      this.expandedUrls.delete(id);
    } else {
      this.expandedUrls.add(id);
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.showSnackBar('Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy: ', err);
      this.showSnackBar('Failed to copy URL!');
    });
  } 

  onVersionChange(selectedVersion) {
    this.minorVersions = this.majorVersionsWiithBuilds.filter(version =>
        version.majorVersion === selectedVersion)[0].minorVersions.map(build => build.name);
    this.minorVersions.sort((a, b) => b.localeCompare(a, undefined, {numeric: true, sensitivity: 'base'}));
    this.awsWorkspaceForm.get('minorVersion').setValue(this.minorVersions[0]);
    this.awsWorkspaceForm.get('minorVersion').enable();
  }


  ngOnInit(): void {
    this.awsWorkspaceForm = new UntypedFormGroup({
      courseID: new UntypedFormControl('',  [Validators.required, Validators.pattern('[a-zA-Z0-9-\-]*[a-zA-Z0-9]$') ]),
      courseName: new UntypedFormControl('',  [Validators.required]),
      gitURL: new UntypedFormControl('',  [Validators.required] ),
      majorVersion: new UntypedFormControl({value: 'Loading', disabled: true}, [Validators.required]),
      minorVersion: new UntypedFormControl({value: 'Loading', disabled: true}),  
      vcs:  new UntypedFormControl('', [Validators.required] ),
      bundle:  new UntypedFormControl('', [Validators.required] ),
    });

  }

  showSnackBar(message) {
    this.popUp.openFromComponent(InfoPopUpComponent, {

      data: { message },
      duration: 5000
    });
  }

  
  showErrorMessage(message: string) {
    this.dialog.open(InfoDialogComponent, {
      width: '450px',
      data: { "title" : "Error", "message": message},
    });

  }


  errorHandling(control: string, error: string) {  
    return this.awsWorkspaceForm.controls[control].hasError(error);
  }
  
  private openSubmitDialog(title: string, message: string): MatDialogRef<SubmitDialogComponent, any> {
    return this.dialog.open(SubmitDialogComponent, {
        width: '450px',
        data: { title, message }
    });
  }

  private openEditDialog(courseToEdit) {
    return this.dialog.open(EditCourseDialogComponent, {
      width: '500px',
      data: {
        minorVersionList: this.minorVersions,
        majorVersions: this.majorVersions,
        majorVersionsWiithBuilds: this.majorVersionsWiithBuilds,
        courseToEdit: courseToEdit,
        bundles: this.allBundles
      },
      disableClose: true
    });
  }

  private openInfoDialog(title: string, message: string): void {
    this.dialog.open(InfoDialogComponent, {
        width: '450px',
        data: { title, message }
    });
  }

}
